import { motion } from "framer-motion";
import { Home, Video, Calendar, Baby, Droplets, Sparkles, HeartHandshake, Check, Flower2 } from "lucide-react";

const ATELIERS = [
    {
        icon: HeartHandshake,
        title: "Accompagnement de la grossesse au post-partum en passant par l'accouchement",
        benefits: [
            "Apporter des informations fiables pour mieux comprendre les changements physiques et émotionnels",
            "Aider à la naissance et à mettre en place un projet de naissance",
            "Réduire le stress et les inquiétudes",
            "Soutenir la récupération physique et émotionnelle après la naissance",
            "Favoriser le lien parent-bébé et l'équilibre familial",
        ],
        testid: "service-atelier-1",
    },
    {
        icon: Baby,
        title: "Atelier autour de l'allaitement maternel",
        benefits: [
            "Connaître les positions adaptées",
            "Aborder le tire-allaitement",
            "Astuces et outils pour faciliter votre allaitement",
            "Prévention et identification des éventuelles pathologies / difficultés",
            "Soutien ou urgences allaitement",
        ],
        testid: "service-atelier-2",
    },
    {
        icon: Droplets,
        title: "Accompagnement au bain enveloppé de la naissance à 3 mois",
        benefits: [
            "Permet au bébé une approche douce du bain",
            "Retrouver des sensations familières de l'in-utero",
            "Apaise les tensions",
            "Favorise le lien d'attachement",
            "Contribue au sommeil et à la détente",
            "Soulage les pleurs",
            "Apaise les maux de ventre",
        ],
        testid: "service-atelier-3",
    },
    {
        icon: Sparkles,
        title: "Atelier massage bien-être bébé",
        benefits: [
            "Apaise et sécurise le bébé",
            "Aide à soulager certains maux (maux de ventre, sphère ORL, douleurs dentaires…)",
        ],
        testid: "service-atelier-4",
    },
];

const PONCTUELLES = [
    {
        icon: Home,
        title: "Consultation ou atelier à domicile",
        duration: "1h",
        price: "60€",
        testid: "service-ponctuel-1",
    },
    {
        icon: Home,
        title: "Consultation à domicile",
        duration: "1h30",
        price: "90€",
        testid: "service-ponctuel-2",
    },
    {
        icon: Video,
        title: "Consultation en visio",
        duration: "1h",
        price: "50€",
        testid: "service-ponctuel-3",
    },
];

const OFFRES = [
    {
        duration: "1 mois",
        sessions: "2 rendez-vous",
        price: "90€",
        testid: "service-offre-1",
    },
    {
        duration: "1 mois",
        sessions: "4 rendez-vous",
        price: "180€",
        testid: "service-offre-2",
    },
    {
        duration: "2 mois",
        sessions: "8 rendez-vous",
        price: "350€",
        featured: true,
        testid: "service-offre-4",
    },
    {
        duration: "3 mois",
        sessions: "6 rendez-vous",
        price: "290€",
        testid: "service-offre-3",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="py-24 md:py-36 bg-cream"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl"
                >
                    <span className="text-xs tracking-[0.25em] uppercase text-rose-700">
                        Services
                    </span>
                    <h2
                        className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink mt-4 leading-tight"
                        data-testid="services-title"
                    >
                        Des accompagnements
                        <br />
                        pensés <span className="italic text-rose-700">pour vous</span>.
                    </h2>
                    <p className="mt-6 text-ink-soft leading-relaxed">
                        Que vous souhaitiez un rendez-vous ponctuel ou un suivi sur
                        plusieurs mois, voici les formules disponibles. Chaque séance
                        peut être adaptée à votre projet de parentalité.
                    </p>
                </motion.div>

                {/* Banner photos (avant les ateliers) */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mt-16 grid grid-cols-2 gap-4 md:gap-6"
                    data-testid="ateliers-banner"
                >
                    <div className="relative aspect-[4/3] md:aspect-[5/4] rounded-3xl overflow-hidden bg-sand">
                        <img
                            src="https://customer-assets.emergentagent.com/job_cindy-naissance/artifacts/nj43f2g2_massage-bebe-scaled.jpg"
                            alt="Massage bien-être bébé — pieds de nourrisson tenus tendrement"
                            className="w-full h-full object-cover"
                            loading="lazy"
                            data-testid="ateliers-banner-massage"
                        />
                    </div>
                    <div className="relative aspect-[4/3] md:aspect-[5/4] rounded-3xl overflow-hidden bg-sand">
                        <img
                            src="https://customer-assets.emergentagent.com/job_cindy-naissance/artifacts/r3r8o27a_bain-enveloppe.jpg"
                            alt="Bain enveloppé — bébé apaisé dans l'eau"
                            className="w-full h-full object-cover"
                            loading="lazy"
                            data-testid="ateliers-banner-bain"
                        />
                    </div>
                </motion.div>

                {/* Ateliers thématiques */}
                <div className="mt-16" data-testid="ateliers-thematiques">
                    <h3 className="font-serif text-2xl md:text-3xl text-ink mb-2">
                        Ateliers thématiques
                    </h3>
                    <p className="text-sm text-ink-soft mb-8 max-w-2xl">
                        Des ateliers pratiques et bienveillants, pensés pour vous
                        accompagner dans les premiers gestes et les premières
                        attentions du quotidien avec bébé.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-6">
                        {ATELIERS.map((a, i) => {
                            const Icon = a.icon;
                            return (
                                <motion.article
                                    key={a.testid}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.7, delay: i * 0.08 }}
                                    data-testid={a.testid}
                                    className="group bg-sand/50 border border-line rounded-3xl p-8 hover:bg-sand hover:border-rose-300 transition-all duration-500 flex flex-col"
                                >
                                    <div className="flex items-start gap-4 mb-5">
                                        <div className="w-12 h-12 shrink-0 rounded-full bg-cream flex items-center justify-center border border-line group-hover:border-rose-300 transition-colors">
                                            <Icon
                                                size={20}
                                                className="text-rose-700"
                                                strokeWidth={1.5}
                                            />
                                        </div>
                                        <h4 className="font-serif text-2xl text-ink leading-snug pt-1">
                                            {a.title}
                                        </h4>
                                    </div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-rose-700 mb-4 pt-2 border-t border-line">
                                        Les bienfaits
                                    </p>
                                    <ul className="space-y-3">
                                        {a.benefits.map((b, idx) => (
                                            <li
                                                key={idx}
                                                className="flex items-start gap-3 text-sm text-ink-soft leading-relaxed"
                                                data-testid={`${a.testid}-benefit-${idx}`}
                                            >
                                                <Check
                                                    size={16}
                                                    className="text-rose-700 shrink-0 mt-0.5"
                                                    strokeWidth={2}
                                                />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>

                {/* Consultations ponctuelles */}
                <div className="mt-20">
                    <h3 className="font-serif text-2xl md:text-3xl text-ink mb-8">
                        Consultations & ateliers
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                        {PONCTUELLES.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.testid}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.7, delay: i * 0.1 }}
                                    data-testid={s.testid}
                                    className="group bg-sand/50 border border-line rounded-3xl p-8 hover:bg-sand transition-colors duration-500"
                                >
                                    <div className="w-12 h-12 rounded-full bg-cream flex items-center justify-center mb-6 border border-line group-hover:border-rose-300 transition-colors">
                                        <Icon
                                            size={20}
                                            className="text-rose-700"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <h4 className="font-serif text-2xl text-ink leading-snug mb-2">
                                        {s.title}
                                    </h4>
                                    <p className="text-sm text-ink-soft mb-6">
                                        Durée · {s.duration}
                                    </p>
                                    <div className="flex items-baseline gap-1 pt-6 border-t border-line">
                                        <span className="font-serif text-4xl text-rose-700">
                                            {s.price}
                                        </span>
                                        <span className="text-xs text-ink-soft tracking-wide">
                                            / séance
                                        </span>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Offres */}
                <div className="mt-20">
                    <div className="flex items-baseline justify-between gap-4 mb-8 flex-wrap">
                        <h3 className="font-serif text-2xl md:text-3xl text-ink">
                            Offres d'accompagnement
                        </h3>
                        <p className="text-sm text-ink-soft">
                            <Calendar size={14} className="inline mr-2 -mt-0.5" />
                            Suivi régulier sur plusieurs semaines
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {OFFRES.map((o, i) => (
                            <motion.div
                                key={o.testid}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                data-testid={o.testid}
                                className={`relative rounded-3xl p-8 transition-all duration-500 ${
                                    o.featured
                                        ? "bg-rose-700 text-cream border border-rose-700"
                                        : "bg-cream border border-line hover:border-rose-300"
                                }`}
                            >
                                {o.featured && (
                                    <span className="absolute -top-3 left-8 bg-cream text-rose-700 text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-rose-300">
                                        Le plus complet
                                    </span>
                                )}
                                <p
                                    className={`text-xs tracking-[0.2em] uppercase mb-3 ${
                                        o.featured ? "text-cream/80" : "text-rose-700"
                                    }`}
                                >
                                    {o.duration}
                                </p>
                                <p
                                    className={`font-serif text-2xl mb-6 ${
                                        o.featured ? "text-cream" : "text-ink"
                                    }`}
                                >
                                    {o.sessions}
                                </p>
                                <div
                                    className={`pt-6 border-t ${
                                        o.featured ? "border-cream/30" : "border-line"
                                    }`}
                                >
                                    <span
                                        className={`font-serif text-5xl ${
                                            o.featured ? "text-cream" : "text-rose-700"
                                        }`}
                                    >
                                        {o.price}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <p
                        className="mt-12 text-sm text-ink-soft text-center italic"
                        data-testid="services-note"
                    >
                        Tarifs valables sur Montpellier et alentours. N'hésitez pas à me
                        contacter pour discuter de votre projet.
                    </p>
                </div>
            </div>
        </section>
    );
}
